import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { LearningResource, LearningResourceBody } from 'src/app/models/learning_resource.model';
import { Skill } from 'src/app/models/skill.model';
import { ApiService } from 'src/app/services';

export interface LearningResourceFormDialogData {
  mode: 'create' | 'edit';
  learning_resource?: LearningResource;
}

@Component({
  selector: 'app-learning-resource-form-dialog',
  templateUrl: './learning-resource-form-dialog.component.html',
  styleUrl: './learning-resource-form-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
})
export class LearningResourceFormDialogComponent implements OnInit {
  private readonly dialog_ref = inject(MatDialogRef<LearningResourceFormDialogComponent, boolean>);
  private readonly api_service = inject(ApiService);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly form_builder = inject(FormBuilder);

  readonly data = inject<LearningResourceFormDialogData>(MAT_DIALOG_DATA);

  readonly loading = signal(false);
  readonly skills = signal<Skill[]>([]);
  readonly is_edit_mode = this.data.mode === 'edit';

  readonly form = this.form_builder.group({
    name: [this.data.learning_resource?.name ?? '', [Validators.required, Validators.maxLength(255)]],
    expiration_in_months: [this.data.learning_resource?.expiration_in_months ?? 12, [Validators.required, Validators.min(1)]],
    duration_in_hours: [this.data.learning_resource?.duration_in_hours ?? (null as number | null)],
    url: [this.data.learning_resource?.url ?? ''],
    skill_ids: [this.data.learning_resource?.skills?.map(skill => skill.id) ?? ([] as number[])],
  });

  ngOnInit(): void {
    this.api_service
      .getSkills({ per_page: 200, order_by: 'name:asc' })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<Skill>): void => {
          this.skills.set(response.data);
        },
      });
  }

  save(): void {
    if (this.loading() || this.form.invalid) {
      return;
    }

    this.loading.set(true);

    const form_value = this.form.getRawValue();
    const body: LearningResourceBody = {
      name: form_value.name!,
      expiration_in_months: form_value.expiration_in_months!,
      duration_in_hours: form_value.duration_in_hours,
      url: form_value.url || null,
      skill_ids: form_value.skill_ids ?? [],
    };

    const request$ =
      this.is_edit_mode && this.data.learning_resource
        ? this.api_service.updateLearningResource(this.data.learning_resource.id, body)
        : this.api_service.createLearningResource(body);

    request$.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe({
      next: (): void => this.dialog_ref.close(true),
      error: (err: { error?: { message?: string }; message?: string }): void => {
        this.loading.set(false);
        const message = err?.error?.message ?? err?.message ?? 'Error al guardar el recurso formativo.';
        this.snack_bar.open(message, undefined, { duration: 5000 });
      },
    });
  }

  close(): void {
    if (!this.loading()) {
      this.dialog_ref.close(false);
    }
  }
}
